<script setup lang="ts">
// https://reka-ui.com/docs/components/combobox
import type { LocationApiSearchResult } from '@@/shared/location/types';

defineProps<{
  hint?: string
  placeholder?: string
}>();

const selectedLocation = defineModel<LocationApiSearchResult>();
const searchTerm = ref('');
const debouncedSearchTerm = refDebounced(searchTerm, 300);
const { data: locationSearchResponse, isPending } = useSearchLocations(debouncedSearchTerm);
const searchResults = computed(() => locationSearchResponse.value?.items ?? []);
const hasPerformedRequest = computed(() => debouncedSearchTerm.value.trim().length > 1 && !isPending.value);

function highlightSearchTerm(text: string): string {
  if (!searchTerm.value) return text;
  const searchWords = searchTerm.value.split(' ');
  const textWords = text.split(' ').map((word) => {
    const searchWord = searchWords.find(searchWord => word.toLowerCase().includes(searchWord.toLowerCase()));
    if (searchWord) return word.replace(new RegExp(searchWord, 'gi'), match => `<b>${match}</b>`);
    return word;
  });
  return textWords.join(' ');
}

// CSS var --reka-select-trigger-width isn't computed as we want
const anchorRef = ref<HTMLElement | null>(null);
const { width } = useElementBounding(anchorRef);
</script>

<template>
  <div>
    <ComboboxRoot
      v-model="selectedLocation"
      class="relative"
    >
      <ComboboxAnchor ref="anchorRef">
        <div
          class="input relative border rounded-md border-neutral-30 focus-within:border-secondary bg-white text-neutral-10
            group-[.is-prestige]:rounded-none group-[.is-prestige]:font-title"
        >
          <ComboboxInput
            v-model="searchTerm"
            :placeholder
            class="px-s py-xs w-full bg-transparent group-[.is-prestige]:py-s group-[.is-prestige]:px-m"
          />
          <Icon
            name="iad-icon:search"
            class="absolute top-1/2 right-s -translate-y-1/2 pointer-events-none size-6"
          />
        </div>
      </ComboboxAnchor>
      <ComboboxContent
        class="max-h-60 overflow-auto overscroll-contain
          rounded-md bg-white text-neutral-10 ring-1 ring-neutral-60 shadow-popover
          will-change-[transform,opacity] data-[state=open]:animate-slideDownAndFade empty:hidden
          group-[.is-prestige]:rounded-none group-[.is-prestige]:font-title"
        position="popper"
        position-strategy="absolute"
        :side-offset="10"
        side="bottom"
        :style="{ width: `${width}px`, zIndex: '50' }"
      >
        <ComboboxVirtualizer
          v-slot="{ option }"
          :options="searchResults"
          :estimate-size="56"
          :text-content="(opt) => opt.displayName"
          class="divide-y divide-neutral-80"
        >
          <ComboboxItem
            class="hover:bg-secondary-70 data-[highlighted]:bg-secondary-70 p-m flex justify-between items-center w-full"
            :value="option"
          >
            <span class="flex gap-xs">
              <span
                class="flex-1"
              >
                <span v-html="highlightSearchTerm(option.displayName)" />
                <span
                  class="empty:hidden before:content-['_('] after:content-[')']"
                  v-html="highlightSearchTerm(option.displayCode)"
                />
              </span>
            </span>
            <span
              v-if="option.isGroup"
              class="text-neutral text-xs"
              v-text="$t(`core.location.geography_fr.all`)"
            />
            <span
              v-else
              class="text-neutral text-xs"
              v-text="$t(`core.location.geography_fr.${option.layerName}`)"
            />
          </ComboboxItem>
        </ComboboxVirtualizer>
        <ComboboxEmpty
          v-if="hasPerformedRequest"
          class="p-m flex items-center"
        >
          <span v-text="$t('core.location.no_location_found')" />
        </ComboboxEmpty>
      </ComboboxContent>
    </ComboboxRoot>
    <small
      class="hidden:empty"
      v-text="hint"
    />
  </div>
</template>
